import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CircularProgress, Box, Typography, Button, Container, Paper, Divider } from '@mui/material'
import { Footer } from '../Qr/components'
import { useTranslation } from 'react-i18next'
import { IMAGES_PATH } from '../../constants/paths'
import { getValidateAuthorization } from '../../services/authorization'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

export const Authorization = () => {
    const { t } = useTranslation()
    const { hash } = useParams()
    
    const [waitResponse, setWaitResponse] = useState(true)
    const [error, setError] = useState(null)
    const [authorizationData, setAuthorizationData] = useState(null)

    useEffect(() => {
        setWaitResponse(true)
        getValidateAuthorization(hash)
            .then(res => { 
                setAuthorizationData(res.data)
                setError(null)
                setWaitResponse(false)
            })
            .catch(err => {
                setError(err.response?.data)
                setAuthorizationData(null)
                setWaitResponse(false)
            })
    }, [hash])

    const renderContent = () => {
        if (waitResponse) {
            return (
                <>
                    <CircularProgress size={80} thickness={4} color="primary" />
                    <Typography variant="h4" mt={2} ml={3.5}>{t(`authorization.reponse.loading.title`)}...</Typography>
                    <Typography>{t(`authorization.reponse.loading.message`)}</Typography>
                </>
            )
        }

        if (authorizationData) {
            return (
                <>
                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
                    <Typography variant="h4" mt={2} gutterBottom>{t(`authorization.reponse.success.title`)}</Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        {t(`authorization.reponse.success.message`)}
                    </Typography>
                    <Box mt={2}>
                        <Typography variant="body1" fontWeight={600}>{t(`authorization.reponse.success.company`)}:</Typography>
                        <Typography variant="body1" color="text.secondary">{authorizationData.entity.name}</Typography>
                    </Box>
                    {authorizationData.entity.cuit && (
                        <Box>
                            <Typography variant="body1" fontWeight={600}>CUIT:</Typography>
                            <Typography variant="body1" color="text.secondary">{authorizationData.entity.cuit}</Typography>
                        </Box>
                    )}
                    <Divider sx={{ my: 2, backgroundColor: 'grey.300' }} />
                    <Box mt={2}>
                        <Typography variant="body1" fontWeight={600}>{t(`authorization.reponse.success.authorized`)}:</Typography>
                        <Typography variant="body1" color="text.secondary">{authorizationData.userToAuthorize.firstname} {authorizationData.userToAuthorize.lastname}</Typography>
                    </Box>
                    {authorizationData.userToAuthorize.cuit && (
                        <Box>
                            <Typography variant="body1" fontWeight={600}>CUIL:</Typography>
                            <Typography variant="body1" color="text.secondary">{authorizationData.userToAuthorize.cuit}</Typography>
                        </Box>
                    )}
                    <Typography variant="body1" color="text.secondary" mt={2}>
                        {t(`authorization.reponse.success.finalMessage`)}
                    </Typography>
                </>
            )
        }

        const errorMessages = {
            'TokenAuthorized': {
                icon: <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />, 
                title: t(`authorization.reponse.error.TokenAuthorized.title`)
            },
            'TokenExpiredError': {
                icon: <WarningAmberIcon sx={{ fontSize: 80, color: 'warning.main' }} />, 
                title: t(`authorization.reponse.error.TokenExpiredError.title`),
                message: t(`authorization.reponse.error.TokenExpiredError.message`)
            },
            'JsonWebTokenError': {
                icon: <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />, 
                title: t(`authorization.reponse.error.JsonWebTokenError.title`),
                message: t(`authorization.reponse.error.JsonWebTokenError.message`)
            },
            'default': {
                icon: <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />, 
                title: t(`authorization.reponse.error.default.title`),
                message: t(`authorization.reponse.error.default.message`),
                contact: <a href='mailto:info@origino.io'>info@origino.io</a>
            }
        }

        const { icon, title, message, contact } = errorMessages[error] || errorMessages['default']

        return (
            <>
                {icon}
                <Typography variant="h4" mt={2}>{title}</Typography>
                <Typography>{message} {contact}</Typography>
            </>
        )
    }

    return (
        <Container 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                width: '100%', 
                height: '100vh', 
                textAlign: 'center' 
            }}
        >
            <Box sx={{ textAlign: 'center', mt: 3, mb: 2, mr: 4 }}>
                <img src={`${IMAGES_PATH}/origino_logo.png`} alt="logo origino" style={{ width: '250px', height: 'auto' }} />
            </Box>
            <Box>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2, maxWidth: 1000 }}>
                    {renderContent()}
                </Paper>
                {!waitResponse &&
                    <Button 
                        href="/login" 
                        variant="contained" 
                        sx={{ 
                            mt: 3, 
                            '&:hover': {
                              backgroundColor: '#2F54B8',
                              color: 'white'
                            },
                        }}
                    >
                        {t(`authorization.buttons.back`)}
                    </Button>
                }
            </Box>
            <Box sx={{mt: 2, mr: 4}}>
                <Footer tt={t} />
            </Box>
        </Container>
    )
}